import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { itineraryApi } from "@/lib/api";
import { invalidateAfterItineraryWrite } from "@/lib/itineraryCache";
import { findTextEvent } from "./tripTextEvents";
import type { ItineraryResponse, Route } from "@/lib/types";
import TripWeekCalendar from "./TripWeekCalendar";
import type { CalendarEvent, CalendarFlight } from "./tripCalendarView";

/** The viewer's local calendar date, as YYYY-MM-DD (not the UTC date). */
function localToday(): string {
  const n = new Date();
  const p = (x: number) => String(x).padStart(2, "0");
  return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
}

function localWeekStart(): 0 | 1 {
  try {
    const info = (new Intl.Locale(navigator.language) as unknown as { weekInfo?: { firstDay: number } }).weekInfo;
    return info?.firstDay === 1 ? 1 : 0;
  } catch {
    return 0;
  }
}

/** Shares the ["itinerary", id] cache with the day view, so it costs no extra request. */
export default function TripCalendarStrip({
  tripId,
  routes,
  description,
  onAddEvent,
}: {
  tripId: string;
  routes: Route[];
  /** Trip description / original request, mined for an unconfirmed event. */
  description?: string | null;
  onAddEvent?: () => void;
}) {
  const { data } = useQuery<ItineraryResponse>({
    queryKey: ["itinerary", tripId],
    queryFn: () => itineraryApi.get(tripId),
    enabled: !!tripId,
  });

  const qc = useQueryClient();
  const confirm = useMutation({
    mutationFn: (e: CalendarEvent) =>
      itineraryApi.createItem(tripId, { item_type: "event", title: e.title, day_date: e.date }),
    onSuccess: () => invalidateAfterItineraryWrite(qc, tripId),
  });

  const flights: CalendarFlight[] = routes
    .filter((r) => r.leg_role === "outbound" || r.leg_role === "return")
    .map((r) => ({
      date: r.departure_date,
      role: r.leg_role as "outbound" | "return",
      primary: r.is_primary !== false,
    }));

  const events: CalendarEvent[] = [];
  for (const day of data?.days ?? []) {
    for (const seg of day.segments) {
      if (seg.kind === "item" && (seg.item_type === "event" || seg.item_type === "activity")) {
        events.push({ date: seg.day_date, endDate: seg.end_date, title: seg.title });
      }
    }
  }

  // Only propose from text when the itinerary (once loaded) holds no event yet.
  if (data && events.length === 0) {
    const found = findTextEvent(description, flights.map((f) => f.date));
    if (found) events.push({ date: found.date, title: found.title, unconfirmed: true });
  }

  return (
    <TripWeekCalendar
      flights={flights}
      events={events}
      today={localToday()}
      weekStart={localWeekStart()}
      onAddEvent={onAddEvent}
      onConfirmEvent={(e) => confirm.mutate(e)}
      confirming={confirm.isPending}
    />
  );
}
